/**
 * OPS-11.5: Contextual Help Tooltip
 *
 * Reusable tooltip component for Control Tower dashboard panels.
 * Shows an ℹ icon that reveals explanatory text on hover/focus.
 * Uses a React portal to render in document.body so no overflow/stacking
 * constraint in the panel hierarchy can clip or occlude the tooltip.
 */

import { useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface InfoTooltipProps {
  text: string;
}

export function InfoTooltip({ text }: InfoTooltipProps) {
  const id = useId();
  const iconRef = useRef<HTMLSpanElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  function show() {
    if (iconRef.current) {
      const r = iconRef.current.getBoundingClientRect();
      const top = r.top - 8;
      const left = Math.max(16, Math.min(window.innerWidth - 16, r.left + r.width / 2));
      // If tooltip would clip above viewport, position below the icon instead
      setCoords({ top: top < 40 ? r.bottom + 8 : top, left });
    }
  }

  function hide() {
    setCoords(null);
  }

  return (
    <span className="inline-flex" aria-describedby={id}>
      <span
        ref={iconRef}
        tabIndex={0}
        role="img"
        aria-label="Info"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className="w-4 h-4 inline-flex items-center justify-center rounded-full text-[10px] text-slate-400 cursor-help border border-slate-600 hover:text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500 transition-colors"
      >
        ℹ
      </span>
      {coords !== null &&
        createPortal(
          <span
            id={id}
            role="tooltip"
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              transform: 'translate(-50%, -100%)',
              zIndex: 9999,
            }}
            className="pointer-events-none w-max max-w-xs rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-xs text-slate-300 shadow-xl"
          >
            {text}
          </span>,
          document.body
        )}
    </span>
  );
}
